import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import HeroSection from '@/components/home/HeroSection';
import ProblemStatement from '@/components/home/ProblemStatement';
import BenefitsSection from '@/components/home/BenefitsSection';
import QuoteSection from '@/components/home/QuoteSection';
import CaseStudyCTA from '@/components/home/CaseStudyCTA';
import Footer from "@/components/Footer";
import CustomerLogos from '@/components/CustomerLogos';
import WhoUsesFordje from '@/components/home/WhoUsesFordje';
import heroVisual from "@/assets/antioch-report-dashboard.png.asset.json";

const Index = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Fordje",
    "description": "Fordje delivers complete, accurate regulatory data across every U.S. city — automatically mapped and always up to date",
    "url": "https://www.fordje.com",
    "logo": "https://www.fordje.com/og-image.png",
    "foundingDate": "2024",
    "founders": [{ "@type": "Person", "name": "Gillian Sowray" }],
    "industry": "Construction Technology",
    "serviceArea": { "@type": "Place", "addressCountry": "US" },
    "knowsAbout": [
      "Building codes", "Construction regulations", "Permit requirements",
      "Regulatory data", "Clean technology", "Solar installation",
      "Municipal codes", "Zoning regulations"
    ],
    "offers": {
      "@type": "Service",
      "name": "Regulatory Data Platform",
      "description": "Complete regulatory visibility for construction and clean tech projects",
      "serviceType": "Software as a Service"
    }
  };

  const faqData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is Fordje?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fordje is a live requirements platform for clean energy projects. It gives solar, storage, and EV teams every local regulation and utility requirement for a jurisdiction — from feasibility through inspection — mapped, cited, and kept current."
        }
      },
      {
        "@type": "Question",
        "name": "What kinds of requirements does Fordje cover?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fordje covers design standards, engineering rules, permitting, interconnection, inspection, zoning, utility program rules, and applicable state and local code — pulled from the 80 to 250+ documents that govern a jurisdiction and cited to source."
        }
      },
      {
        "@type": "Question",
        "name": "Which project types does Fordje support?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Residential and commercial solar, battery energy storage systems (BESS), and EV charging projects across the United States."
        }
      },
      {
        "@type": "Question",
        "name": "How does Fordje stay up to date?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Every requirement in a Comprehensive Report is monitored for 2 years. When a rule changes, Fordje alerts the project team. Fordje also tracks approved state and federal changes so teams can plan ahead."
        }
      },
      {
        "@type": "Question",
        "name": "How do teams access Fordje?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Through the Fordje web app, exported CSV or PDF reports, or an API that connects into CRM and project management software."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Fordje — Every requirement for your clean energy projects</title>
        <meta name="description" content="Fordje gives solar, storage, and EV teams every local regulation and utility requirement for a jurisdiction — from feasibility through inspection." />
        <meta name="keywords" content="clean energy regulations, solar permitting, storage interconnection, EV charging requirements, AHJ, utility interconnection, regulatory data" />
        <link rel="preload" as="image" href={heroVisual.url} fetchPriority="high" />
        <link rel="canonical" href="https://www.fordje.com/" />
        <meta property="og:title" content="Fordje — Every requirement for your clean energy projects" />
        <meta property="og:description" content="Every local regulation and utility requirement for solar, storage, and EV projects — from feasibility through inspection." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.fordje.com/" />
        <meta property="og:image" content="https://www.fordje.com/og-image.png" />
        <meta property="og:site_name" content="Fordje" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Fordje — Every requirement for your clean energy projects" />
        <meta name="twitter:description" content="Every local regulation and utility requirement for solar, storage, and EV projects." />
        <meta name="twitter:image" content="https://www.fordje.com/og-image.png" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="author" content="Fordje" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqData)}</script>
      </Helmet>

      <Header variant="default" />
      <main>
        <HeroSection />
        <CustomerLogos />
        <ProblemStatement />
        <BenefitsSection />
        <WhoUsesFordje />
        <QuoteSection />
        <CaseStudyCTA />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
